/* @flow */

import * as React from 'react'
import styled from 'styled-components'
import { ControlLabel as BaseControlLabel, FormGroup } from 'react-bootstrap'
import { ErrorMessage as FormikErrorMessage } from 'formik'

import { ErrorLabel } from '../Forms'

const ControlLabel = ({
  children,
  required = false,
  ...rest
}: {
  children: React.Node,
  required?: boolean,
}) => {
  let label = children
  if (required) {
    label = [label, <RequiredLabel />]
  }

  return <BaseControlLabel {...rest}>{label}</BaseControlLabel>
}

const HelpHint = ({ children }: { children?: React.Node }) => {
  return <span className="help">{children}</span>
}

const RequiredLabel = props => <span className="form-required">*</span>

const SaveButton = ({
  className = 'btn btn-success',
  children,
  disabled,
  icon: defaultIconClass,
  submitting,
  type = 'submit',
  ...rest
}: {
  className?: string,
  children?: React.Node,
  disabled?: boolean,
  icon?: string,
  submitting: boolean,
  type?: string,
}) => {
  const icon = submitting ? 'fa fa-spinner fa-spin' : defaultIconClass

  return (
    <button
      type={type}
      className={className}
      disabled={disabled === true || submitting === true}
      {...rest}
    >
      <span className={icon} /> {children}
    </button>
  )
}

const ErrorMessage = ({ children, ...rest }) => {
  return (
    <FormikErrorMessage {...rest}>
      {children ? children : DefaultErrorErrorMessage}
    </FormikErrorMessage>
  )
}

const DefaultErrorErrorMessage = error => <ErrorLabel>{error}</ErrorLabel>

const Fieldset = props => {
  const {
    children,
    last,
    overflowable = false,
    stacked = false,
    ...rest
  } = props
  return (
    <fieldset {...rest} className={last ? 'last' : ''}>
      <FieldsetContainer overflowable={overflowable} stacked={stacked}>
        {React.Children.map(children, child => {
          return React.cloneElement(child, {
            overflowable,
            stacked,
          })
        })}
      </FieldsetContainer>
    </fieldset>
  )
}

const FieldsetContainer = styled.div`
  display: ${props => (props.stacked ? 'block' : 'flex')};

  @media (max-width: 1399px) {
    display: block;
    margin-top: 0;
    margin-right: -20px;
    margin-bottom: 20px;
    margin-left: -20px;
    padding-top: 0;
    padding-right: ${props => (props.overflowable ? 0 : '25px')};
    padding-bottom: 10px;
    padding-left: 25px;
  }
`

Fieldset.defaultProps = {
  last: false,
}

Fieldset.Description = props => {
  const { children, danger, required = false, title, ...restProps } = props
  return (
    <FieldsetDescriptionContainer {...restProps}>
      <h4 className={danger ? 'color-red' : ''}>
        {title}
        {required && <RequiredLabel />}
      </h4>
      {children}
    </FieldsetDescriptionContainer>
  )
}

const FieldsetDescriptionContainer = styled.div`
  flex: 1;
  min-width: calc(100% / 6);

  @media (max-width: 1399px) {
    float: none;
    margin-bottom: 15px;
    margin-left: -25px;
    margin-right: -25px;
    padding: 0 25px 5px 25px;
    width: auto;
  }
`

Fieldset.Form = ({
  children,
  overflowable = false,
  flexWidth = 5,
  stacked,
}) => {
  const style = React.useMemo(
    () => ({
      flex: flexWidth,
      overflowX: overflowable ? 'scroll' : 'visible',
      paddingLeft: stacked ? 0 : '20px',
    }),
    [overflowable, flexWidth, stacked]
  )

  return (
    <FieldsetFormsContainer style={style}>{children}</FieldsetFormsContainer>
  )
}

const FieldsetFormsContainer = styled.div`
  @media (max-width: 1399px) {
    float: none;
    padding-left: 0;
    width: 100%;
  }
`

export { ControlLabel, Fieldset, HelpHint, ErrorMessage, FormGroup, SaveButton }
